import type { CSSProperties } from "react";
import { useAttachmentMetadata } from "@circle-react-shared/BuilderUI/Media/AttachmentContext";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { useParseStyles } from "./useParseStyles";

export interface StylesBackgroundImageValue {
  placeholder: string;
  signed_id: string;
}

interface UseTopBlockStylesProps {
  defaultStyles: Record<string, unknown>;
  ignoreDefaults?: boolean;
  styles?: Record<string, StyleValue>;
}

interface UseTopBlockStylesReturn {
  backgroundStyles: CSSProperties;
  borderColorStyles: CSSProperties;
  marginStyles: CSSProperties;
  paddingStyles: CSSProperties;
}

export const useTopBlockStyles = ({
  styles = {},
  defaultStyles,
  ignoreDefaults = true,
}: UseTopBlockStylesProps): UseTopBlockStylesReturn => {
  const { backgroundImage } = styles;
  const { value } = backgroundImage || {};

  const { signed_id: backgroundImageSignedId, placeholder } =
    (value as StylesBackgroundImageValue) || {
      signed_id: "",
      placeholder: "",
    };

  const attachmentMetadata = useAttachmentMetadata(backgroundImageSignedId);
  const { url: attachmentUrl } = attachmentMetadata ?? {};
  const url = attachmentUrl || placeholder;

  const baseBackgroundStyles = url
    ? {
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center center",
        backgroundSize: "cover",
      }
    : undefined;

  const backgroundStyles = useParseStyles({
    styles: {
      backgroundImage: {
        type: "background-image",
        value: url,
      },
      backgroundColor: styles.backgroundColor,
    },
    defaultStyles: {
      backgroundImage: defaultStyles.backgroundImage,
      backgroundColor: defaultStyles.backgroundColor,
    },
    baseStyles: baseBackgroundStyles,
  });

  const paddingStyles = useParseStyles({
    styles: { padding: styles.padding },
    defaultStyles: { padding: defaultStyles.padding },
    baseStyles: ignoreDefaults ? {} : undefined,
  });

  const marginStyles = useParseStyles({
    styles: { margin: styles.margin },
    defaultStyles: { margin: defaultStyles.margin },
    baseStyles: ignoreDefaults ? {} : undefined,
  });

  const borderColorStyles = useParseStyles({
    styles: { borderColor: styles.borderColor },
    defaultStyles: { borderColor: defaultStyles.borderColor },
    baseStyles: ignoreDefaults ? {} : undefined,
  });

  return {
    backgroundStyles,
    borderColorStyles,
    marginStyles,
    paddingStyles,
  };
};
